import { useState, useEffect } from 'react';

interface ProgressProps {
  userProfile: any;
}

interface CourseProgress {
  id: string;
  title: string;
  instructor_name: string;
  total_assignments: number;
  completed_assignments: number;
  graded_assignments: number;
  average_score: number;
  progress_percentage: number;
  total_score: number;
  max_total_score: number;
}

export default function Progress({ userProfile }: ProgressProps) {
  const [loading, setLoading] = useState(true);
  const [coursesProgress, setCoursesProgress] = useState<CourseProgress[]>([]);
  const [overallStats, setOverallStats] = useState({
    totalCourses: 0,
    totalAssignments: 0,
    completedAssignments: 0,
    averageScore: 0,
    overallProgress: 0
  });

  useEffect(() => {
    if (userProfile) {
      fetchProgressData();
    }
  }, [userProfile]);

  const getAuthToken = () => {
    const session = localStorage.getItem('supabase_session');
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        return sessionData.access_token;
      } catch (e) {
        console.error('Error parsing session:', e);
      }
    }
    return import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  };

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();

      // Lấy dữ liệu bài tập của học sinh
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-student-assignments?student_id=${userProfile.id}`,
        {
          method: 'GET',
          headers: {
            'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const courses = data.courses || [];

        // Tính toán tiến độ từng khóa học
        const progressData: CourseProgress[] = courses.map((course: any) => {
          const assignments = course.assignments || [];
          const totalAssignments = assignments.length;
          
          // Đếm số bài tập đã hoàn thành (đã nộp hoặc đã chấm)
          const completedAssignments = assignments.filter(
            (a: any) => a.status === 'submitted' || a.status === 'graded'
          ).length;
          
          // Đếm số bài tập đã được chấm điểm
          const gradedAssignments = assignments.filter(
            (a: any) => a.status === 'graded' && a.score !== null
          );
          
          // Tính điểm trung bình
          let averageScore = 0;
          let totalScore = 0;
          let maxTotalScore = 0;
          
          if (gradedAssignments.length > 0) {
            totalScore = gradedAssignments.reduce((sum: number, a: any) => sum + (a.score || 0), 0);
            maxTotalScore = gradedAssignments.reduce((sum: number, a: any) => sum + (a.max_score || 0), 0);
            averageScore = maxTotalScore > 0 ? (totalScore / maxTotalScore) * 100 : 0;
          }

          // Tính phần trăm tiến độ dựa trên số bài tập đã hoàn thành
          const progressPercentage = totalAssignments > 0 
            ? Math.round((completedAssignments / totalAssignments) * 100) 
            : 0;

          return {
            id: course.id,
            title: course.title,
            instructor_name: course.instructor_name,
            total_assignments: totalAssignments,
            completed_assignments: completedAssignments,
            graded_assignments: gradedAssignments.length,
            average_score: Math.round(averageScore),
            progress_percentage: progressPercentage,
            total_score: totalScore,
            max_total_score: maxTotalScore
          };
        });

        setCoursesProgress(progressData);

        // Tính toán thống kê tổng quan
        const totalCourses = progressData.length;
        const totalAssignments = progressData.reduce((sum, c) => sum + c.total_assignments, 0);
        const completedAssignments = progressData.reduce((sum, c) => sum + c.completed_assignments, 0);
        
        // Tính điểm trung bình tổng
        const totalScore = progressData.reduce((sum, c) => sum + c.total_score, 0);
        const maxTotalScore = progressData.reduce((sum, c) => sum + c.max_total_score, 0);
        const averageScore = maxTotalScore > 0 ? Math.round((totalScore / maxTotalScore) * 100) : 0;
        
        // Tính tiến độ tổng thể
        const overallProgress = totalAssignments > 0 
          ? Math.round((completedAssignments / totalAssignments) * 100) 
          : 0;

        setOverallStats({
          totalCourses,
          totalAssignments,
          completedAssignments,
          averageScore,
          overallProgress
        });
      }
    } catch (error) {
      console.error('Error fetching progress data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải dữ liệu tiến độ...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tiến độ học tập</h1>
          <p className="text-gray-600">Theo dõi quá trình học tập của bạn</p>
        </div>
        <button
          onClick={fetchProgressData}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-book-line text-2xl"></i>
            </div>
            <span className="text-3xl font-bold">{overallStats.totalCourses}</span>
          </div>
          <p className="text-blue-100">Tổng khóa học</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-line text-2xl"></i>
            </div>
            <span className="text-3xl font-bold">{overallStats.completedAssignments}/{overallStats.totalAssignments}</span>
          </div>
          <p className="text-green-100">Bài tập hoàn thành</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-star-line text-2xl"></i>
            </div>
            <span className="text-3xl font-bold">{overallStats.averageScore}%</span>
          </div>
          <p className="text-purple-100">Điểm trung bình</p>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Tiến độ tổng thể</h2>
          <span className="text-2xl font-bold text-blue-600">{overallStats.overallProgress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-500"
            style={{ width: `${overallStats.overallProgress}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Bạn đã hoàn thành {overallStats.completedAssignments} trong tổng số {overallStats.totalAssignments} bài tập
        </p>
      </div>

      {/* Course Progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Tiến độ từng khóa học</h2>
        
        {coursesProgress.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-book-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có khóa học nào</h3>
            <p className="text-gray-500">Bạn chưa được thêm vào khóa học nào</p>
          </div>
        ) : (
          <div className="space-y-6">
            {coursesProgress.map((course) => (
              <div key={course.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{course.title}</h3>
                    <p className="text-sm text-gray-600">
                      <i className="ri-user-line mr-1"></i>
                      Giảng viên: {course.instructor_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-blue-600">{course.progress_percentage}%</div>
                    <div className="text-xs text-gray-500">Tiến độ</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${course.progress_percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex items-center text-blue-600 mb-1">
                      <i className="ri-file-list-line mr-1"></i>
                      <span className="text-xs font-medium">Tổng bài tập</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900">{course.total_assignments}</div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="flex items-center text-green-600 mb-1">
                      <i className="ri-check-line mr-1"></i>
                      <span className="text-xs font-medium">Đã hoàn thành</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900">{course.completed_assignments}</div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="flex items-center text-purple-600 mb-1">
                      <i className="ri-medal-line mr-1"></i>
                      <span className="text-xs font-medium">Đã chấm điểm</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900">{course.graded_assignments}</div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="flex items-center text-orange-600 mb-1">
                      <i className="ri-star-line mr-1"></i>
                      <span className="text-xs font-medium">Điểm TB</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900">
                      {course.graded_assignments > 0 ? `${course.average_score}%` : '-'}
                    </div>
                  </div>
                </div>

                {/* Score Details */}
                {course.graded_assignments > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Tổng điểm đạt được:</span>
                      <span className="font-semibold text-gray-900">
                        {course.total_score}/{course.max_total_score} điểm
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
